/** TS port of `Renderer.kt`. */

import type { BgMode } from "./settings.js";

export interface RedactionRect {
  rect: { left: number; top: number; right: number; bottom: number };
  source: "AUTO" | "MANUAL";
  fillMode: "SOLID" | "SHUFFLE";
  /** ARGB-packed (0xAARRGGBB), used when fillMode === "SOLID". */
  fillColor: number;
  label?: string;
}

export const red = (c: number) => (c >>> 16) & 0xff;
export const green = (c: number) => (c >>> 8) & 0xff;
export const blue = (c: number) => c & 0xff;
export const argb = (a: number, r: number, g: number, b: number) =>
  ((a & 0xff) << 24) | ((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff);

// 4-bit-per-channel RGB histogram (4096 buckets) with running sums for
// centroid recovery.
class Histogram4096 {
  counts = new Int32Array(4096);
  rSum = new Float64Array(4096);
  gSum = new Float64Array(4096);
  bSum = new Float64Array(4096);
  add(r: number, g: number, b: number): void {
    const k = (r >> 4) * 256 + (g >> 4) * 16 + (b >> 4);
    this.counts[k]++;
    this.rSum[k] += r;
    this.gSum[k] += g;
    this.bSum[k] += b;
  }
  centroid(idx: number): number {
    const n = this.counts[idx];
    if (n === 0) return 0xff000000;
    return argb(0xff, Math.round(this.rSum[idx] / n), Math.round(this.gSum[idx] / n), Math.round(this.bSum[idx] / n));
  }
}

export function applyFillForMode(
  r: RedactionRect,
  mode: BgMode,
  bgColor: number,
  src: ImageData,
): RedactionRect {
  switch (mode) {
    case "BLACK":
      return { ...r, fillMode: "SOLID", fillColor: 0xff000000 };
    case "COLOR":
      return { ...r, fillMode: "SOLID", fillColor: bgColor | 0xff000000 };
    case "SHUFFLE":
      return { ...r, fillMode: "SHUFFLE" };
    case "DETECT":
    default: {
      const c = sampleEdgeColor(src, r.rect);
      return { ...r, fillMode: "SOLID", fillColor: c };
    }
  }
}

function topTwoColors(pixels: Uint8ClampedArray): { c1: number; c2: number } {
  const h = new Histogram4096();
  for (let i = 0; i < pixels.length; i += 4) h.add(pixels[i], pixels[i + 1], pixels[i + 2]);

  let best = 0;
  let second = 0;
  for (let i = 0; i < 4096; i++) {
    if (h.counts[i] > h.counts[best]) {
      second = best;
      best = i;
    } else if (h.counts[i] > h.counts[second] && i !== best) {
      second = i;
    }
  }
  const c1 = h.centroid(best);
  const c2 = h.counts[second] > 0
    ? h.centroid(second)
    : argb(0xff, 255 - red(c1), 255 - green(c1), 255 - blue(c1));
  return { c1, c2 };
}

/**
 * Generate a `[w*h]` ImageData chunk filled with a 50/50 deterministic mix of
 * the two dominant colours sampled from the rect's source pixels. The seeded
 * RNG matches the Android port's `paintShuffle` — same rect coordinates ⇒
 * same fill, so re-renders are stable.
 */
export function shuffleTile(
  src: ImageData,
  rect: RedactionRect["rect"],
): ImageData | null {
  const pLeft = Math.max(0, rect.left | 0);
  const pTop = Math.max(0, rect.top | 0);
  const pRight = Math.min(src.width, rect.right | 0);
  const pBottom = Math.min(src.height, rect.bottom | 0);
  const w = pRight - pLeft;
  const h = pBottom - pTop;
  if (w <= 0 || h <= 0) return null;

  const sample = new Uint8ClampedArray(w * h * 4);
  for (let y = 0; y < h; y++) {
    const srcRow = ((pTop + y) * src.width + pLeft) * 4;
    sample.set(src.data.subarray(srcRow, srcRow + w * 4), y * w * 4);
  }
  const { c1, c2 } = topTwoColors(sample);

  const out = new Uint8ClampedArray(w * h * 4);
  let seed = (rect.left * 73856093) ^ (rect.top * 19349663) ^ (rect.right * 83492791);
  // xorshift32 — matches the *style* of the Android Random(seed) walk,
  // not bit-for-bit.
  const next = (): boolean => {
    seed ^= seed << 13;
    seed ^= seed >>> 17;
    seed ^= seed << 5;
    return (seed & 1) === 1;
  };
  for (let i = 0; i < w * h; i++) {
    const c = next() ? c1 : c2;
    const o = i * 4;
    out[o] = red(c);
    out[o + 1] = green(c);
    out[o + 2] = blue(c);
    out[o + 3] = 255;
  }
  return new ImageData(out, w, h);
}

function sampleEdgeColor(src: ImageData, rect: RedactionRect["rect"]): number {
  const w = src.width;
  const h = src.height;
  const hist = new Histogram4096();
  let n = 0;
  const margin = 4;

  const push = (x: number, y: number) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const idx = (y * w + x) * 4;
    hist.add(src.data[idx], src.data[idx + 1], src.data[idx + 2]);
    n++;
  };

  for (let x = rect.left; x < rect.right; x += 2) {
    push(x, rect.top - margin);
    push(x, rect.bottom + margin);
  }
  for (let y = rect.top; y < rect.bottom; y += 2) {
    push(rect.left - margin, y);
    push(rect.right + margin, y);
  }
  if (n === 0) return 0xff000000;
  let best = 0;
  for (let i = 1; i < 4096; i++) if (hist.counts[i] > hist.counts[best]) best = i;
  return hist.centroid(best);
}

/** Returns a canvas the caller can `.toBlob()` from. */
export function paintExport(
  source: HTMLCanvasElement,
  rects: readonly RedactionRect[],
  imageData: ImageData,
  scalePercent: number,
): HTMLCanvasElement {
  const sx = Math.max(0.1, Math.min(1, scalePercent / 100));
  const w = source.width;
  const h = source.height;
  const sw = Math.max(1, Math.round(w * sx));
  const sh = Math.max(1, Math.round(h * sx));
  const target = document.createElement("canvas");
  target.width = sw;
  target.height = sh;
  const ctx = target.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d context for export canvas");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, sw, sh);

  for (const r of rects) {
    const left = Math.round(r.rect.left * sx);
    const top = Math.round(r.rect.top * sx);
    const right = Math.round(r.rect.right * sx);
    const bottom = Math.round(r.rect.bottom * sx);
    const rw = right - left;
    const rh = bottom - top;
    if (rw <= 0 || rh <= 0) continue;
    if (r.fillMode === "SHUFFLE") {
      const tile = shuffleTile(imageData, r.rect);
      if (tile) {
        const tmp = document.createElement("canvas");
        tmp.width = tile.width;
        tmp.height = tile.height;
        tmp.getContext("2d")!.putImageData(tile, 0, 0);
        ctx.drawImage(tmp, left, top, rw, rh);
      }
    } else {
      ctx.fillStyle = `rgb(${red(r.fillColor)},${green(r.fillColor)},${blue(r.fillColor)})`;
      ctx.fillRect(left, top, rw, rh);
    }
  }
  return target;
}
